import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Container,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  Dashboard as DashboardIcon,
  Calculate as CalculateIcon,
  Science as ScienceIcon,
  Assessment as AssessmentIcon,
  Support as SupportIcon,
  Verified as VerifiedIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { verifyEmail } from '../../store/slices/authSlice';

const EmailVerification: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, error } = useAppSelector(state => state.auth);

  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorDetails, setErrorDetails] = useState<any>(null);

  useEffect(() => {
    if (!token) {
      setVerificationStatus('error');
      return;
    }

    const verifyEmailToken = async () => {
      try {
        await dispatch(verifyEmail(token)).unwrap();
        setVerificationStatus('success');
      } catch (error: any) {
        setVerificationStatus('error');
        // Store enhanced error details for better user experience
        if (error.details) {
          setErrorDetails(error.details);
        }
      }
    };

    verifyEmailToken();
  }, [token, dispatch]);

  const handleContinue = () => {
    navigate('/login');
  };

  const renderContent = () => {
    switch (verificationStatus) {
      case 'loading':
        return (
          <Container maxWidth="md">
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
              <Box sx={{ mb: 3 }}>
                <img
                  src="/assets/flaha_main_logo.svg"
                  alt="FlahaFAST Logo"
                  style={{ height: 60, marginBottom: 16 }}
                />
              </Box>
              <CircularProgress size={64} sx={{ mb: 3, color: 'primary.main' }} />
              <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                Verifying Your Email
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                Please wait while we verify your email address...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This should only take a moment
              </Typography>
            </Paper>
          </Container>
        );

      case 'success':
        return (
          <Container maxWidth="md">
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
              {/* Header Section */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <img
                  src="/assets/flaha_main_logo.svg"
                  alt="FlahaFAST Logo"
                  style={{ height: 60, marginBottom: 16 }}
                />
                <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                <Typography variant="h3" gutterBottom sx={{ color: 'success.main', fontWeight: 600 }}>
                  🎉 Email Verified Successfully!
                </Typography>
                <Chip
                  icon={<VerifiedIcon />}
                  label="Account Activated"
                  color="success"
                  size="large"
                  sx={{ mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                  Welcome to FlahaFAST! Your professional hydroponic calculation platform is ready.
                </Typography>
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* Features Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, textAlign: 'center' }}>
                  🚀 What You Can Do Now
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <ScienceIcon sx={{ color: 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Water Quality Analysis"
                      secondary="Analyze and optimize your water parameters for perfect growing conditions"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CalculateIcon sx={{ color: 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Nutrient Calculations"
                      secondary="Precise nutrient formulations with chemical-grade accuracy"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <AssessmentIcon sx={{ color: 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Professional Reports"
                      secondary="Generate detailed calculation reports and save your formulations"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <DashboardIcon sx={{ color: 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Complete Dashboard"
                      secondary="Access your personalized hydroponic management center"
                    />
                  </ListItem>
                </List>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleContinue}
                  startIcon={<DashboardIcon />}
                  sx={{
                    minWidth: 250,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #15bfd5 0%, #0b7d9d 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0b7d9d 0%, #15bfd5 100%)',
                    }
                  }}
                >
                  Access Your Dashboard
                </Button>
              </Box>

              {/* Success Message */}
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Account Ready!</strong> You now have full access to all FlahaFAST features.
                  Start by analyzing your water quality or creating your first nutrient formulation.
                </Typography>
              </Alert>
            </Paper>
          </Container>
        );

      case 'error':
        return (
          <Container maxWidth="md">
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
              {/* Header Section */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <img
                  src="/assets/flaha_main_logo.svg"
                  alt="FlahaFAST Logo"
                  style={{ height: 60, marginBottom: 16 }}
                />
                <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                <Typography variant="h4" gutterBottom sx={{ color: 'error.main', fontWeight: 600 }}>
                  Verification Failed
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                  {error || 'The verification link is invalid or has expired.'}
                </Typography>
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* Error Details */}
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>What happened?</strong> {errorDetails?.supportMessage ||
                    'The verification link may have expired or been used already. Verification links are valid for 24 hours for security reasons.'}
                </Typography>
              </Alert>

              {/* Support Information */}
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Need help?</strong> Contact our support team at{' '}
                  <a
                    href={`mailto:${errorDetails?.contactEmail || 'info@flaha.com'}`}
                    style={{ color: '#15bfd5', textDecoration: 'none' }}
                  >
                    {errorDetails?.contactEmail || 'info@flaha.com'}
                  </a>{' '}
                  for immediate assistance with email verification.
                </Typography>
                {errorDetails?.helpUrl && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <a
                      href={errorDetails.helpUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#15bfd5', textDecoration: 'none' }}
                    >
                      📖 View Email Verification Help Guide
                    </a>
                  </Typography>
                )}
              </Alert>

              {/* Action Buttons */}
              <Box sx={{ textAlign: 'center', gap: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleContinue}
                  startIcon={<EmailIcon />}
                  sx={{ minWidth: 200 }}
                >
                  Back to Sign In
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<SupportIcon />}
                  href="mailto:info@flaha.com"
                  sx={{ minWidth: 200 }}
                >
                  Contact Support
                </Button>
              </Box>
            </Paper>
          </Container>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f6f3 0%, #e8f4f8 100%)',
      py: 4,
      px: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {renderContent()}
    </Box>
  );
};

export default EmailVerification;
